import React, { useState, useEffect } from 'react';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { API_BASE } from '../../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';

interface Lead {
    _id: string;
    name: string;
    mobile: string;
    email: string;
    status: string;
    source: string;
    city: string;
    budget: string;
    propertyType: string;
    timeline: { status: string; notes: string; date: string }[];
    siteVisit?: {
        date: string;
        time: string;
        agentName: string;
        project?: { _id: string; projectName: string };
        notes: string;
    };
    createdAt: string;
}

export default function BrokerInquiries() {
    const { token } = useAuth();
    const [inquiries, setInquiries] = useState<Lead[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [viewInquiry, setViewInquiry] = useState<Lead | null>(null);

    useEffect(() => {
        if (token) fetchInquiries();
    }, [token]);

    const fetchInquiries = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/leads`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) {
                // Filter only for "New Inquiry" and "Requirement" for this specific page
                const filtered = (Array.isArray(data) ? data : []).filter(l => 
                    l.status === 'New Inquiry' || l.status === 'Requirement'
                );
                setInquiries(filtered);
            } else {
                setInquiries([]);
                toast.error(data.message || 'Failed to load inquiries');
            }
        } catch (err) {
            toast.error('Failed to load inquiries');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateStatus = async (id: string, status: string) => {
        try {
            const res = await fetch(`${API_BASE}/leads/${id}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ status, notes: `Qualified as ${status} from Inquiries page.` })
            });
            if (res.ok) {
                toast.success(`Inquiry moved to ${status}`);
                // Remove from local list as it's no longer a "new inquiry"
                setInquiries(prev => prev.filter(l => l._id !== id));
            }
        } catch (err) {
            toast.error('Error updating status');
        }
    };

    const filtered = inquiries.filter(l => 
        l.name.toLowerCase().includes(search.toLowerCase()) || 
        l.mobile.includes(search)
    );

    return (
        <UserDashboardLayout>
            <div className="mb-8">
                <h2 className="text-3xl font-black text-blue-950 uppercase italic tracking-tighter">Fresh Inquiries</h2>
                <p className="text-blue-950/60 font-black uppercase tracking-[3px] text-[10px]">Vett and qualify incoming property requests before moving them to your sales pipeline.</p>
            </div>

            <div className="bg-white rounded-[2rem] border border-blue-900/10 shadow-sm overflow-hidden mb-12">
                <div className="p-8 border-b border-blue-900/5 flex flex-col md:flex-row justify-between items-center gap-6 bg-blue-50/10">
                    <div className="relative group w-full md:w-[400px]">
                        <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none">
                            <i className="fa-solid fa-magnifying-glass text-blue-950/20 group-focus-within:text-blue-950 transition-colors"></i>
                        </div>
                        <input 
                            type="text" 
                            className="w-full bg-white border border-blue-900/10 rounded-xl py-4 pl-14 pr-6 text-sm font-black text-blue-950 placeholder:text-blue-950/20 focus:ring-4 focus:ring-blue-500/5 focus:border-blue-950 transition-all uppercase tracking-tight"
                            placeholder="Find inquiry by customer name..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="p-32 text-center text-blue-950/20 font-black uppercase tracking-[5px] text-[10px]">
                            <div className="w-12 h-12 border-4 border-blue-950 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                            Opening Inbox...
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-32 text-center">
                            <div className="w-24 h-24 bg-blue-50/50 rounded-3xl flex items-center justify-center mx-auto mb-6 border border-blue-900/5 text-blue-950/10">
                                <i className="fa-solid fa-envelope-circle-check text-4xl"></i>
                            </div>
                            <h3 className="text-xl font-black text-blue-950 mb-2 uppercase italic">Inbox is Clear</h3>
                            <p className="text-blue-950/40 text-[10px] font-black uppercase tracking-widest max-w-xs mx-auto">No new un-vetted inquiries found at the moment.</p>
                        </div>
                    ) : (
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-blue-50/5 border-b border-blue-900/5">
                                    <th className="px-8 py-5 text-[10px] font-black text-blue-950/40 uppercase tracking-[3px]">Prospect</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-blue-950/40 uppercase tracking-[3px]">Inquiry Details</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-blue-950/40 uppercase tracking-[3px]">Action</th>
                                    <th className="px-8 py-5 text-[10px] font-black text-blue-950/40 uppercase tracking-[3px] text-right">Qualify</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-blue-900/5">
                                {filtered.map(l => (
                                    <tr key={l._id} className="group hover:bg-blue-50/20 transition-all">
                                        <td className="px-8 py-7">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 rounded-xl bg-blue-950 flex items-center justify-center text-white font-black text-lg shadow-lg shadow-blue-900/20 ring-4 ring-white">
                                                    {l.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div className="text-left">
                                                    <h4 className="font-black text-blue-950 group-hover:text-blue-600 transition-colors uppercase text-sm tracking-tight italic leading-tight">{l.name}</h4>
                                                    <p className="text-[10px] font-black text-blue-950/40 uppercase mt-1 tracking-widest leading-none">{l.mobile}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-7">
                                            <div className="text-left">
                                                <h4 className="font-black text-blue-950 uppercase text-[10px] tracking-tight truncate w-40 italic">{l.propertyType || "General Question"}</h4>
                                                <p className="text-[10px] font-black text-blue-600 uppercase mt-1 tracking-widest leading-none opacity-60">Source: {l.source}</p>
                                            </div>
                                        </td>
                                        <td className="px-8 py-7">
                                            <a 
                                                href={`tel:${l.mobile}`}
                                                className="inline-flex items-center gap-2 bg-blue-950 text-white px-5 py-2.5 rounded-xl text-[9px] font-black uppercase tracking-[3px] hover:bg-blue-600 transition-all shadow-lg active:scale-95"
                                            >
                                                <i className="fa-solid fa-phone"></i> Contact Now
                                            </a>
                                        </td>
                                        <td className="px-8 py-7 text-right">
                                            <select
                                                className="appearance-none font-black text-[9px] uppercase tracking-[3px] px-6 py-2.5 rounded-xl border border-blue-900/10 bg-white text-blue-950 cursor-pointer focus:ring-4 focus:ring-blue-500/5 focus:border-blue-950 transition-all text-right shadow-sm"
                                                value={l.status}
                                                onChange={e => handleUpdateStatus(l._id, e.target.value)}
                                            >
                                                <option disabled value="New Inquiry">Move to Pipeline</option>
                                                <option value="Negotiation">Negotiation</option>
                                                <option value="Booked">Booked</option>
                                                <option value="Lost">Lost</option>
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </UserDashboardLayout>
    );
}
